import isAfter from 'date-fns/isAfter'
import isBefore from 'date-fns/isBefore'
import { isBoolean, isEmpty, isEqual, isNumber, trim } from 'lodash'

import { IAutocompleteOutput } from 'components/v2/Autocomplete/types'
import { EConfirmationModality } from 'features/Bookings/types'
import { IEditServiceForm, IPlanning } from 'features/Services/components/EditService/types'
import {
  IItinerary,
  IItinerarySegment
} from 'features/Services/components/NewServicesV2/components/ItinerariesCard/types'
import { ISegmentPlanning } from 'features/Services/components/NewServicesV2/components/SegmentsCard/types'
import { IService } from 'features/Services/components/NewServicesV2/components/ServicesForm/types'
import {
  EVehicleScheduleType,
  IVehicle,
  IVehicleFrequency
} from 'features/Services/components/NewServicesV2/components/VehiclesCard/types'
import { INewServicesForm } from 'features/Services/components/NewServicesV2/types'
import { ICapacityLimit } from 'features/Services/components/NewServicesV2/components/CapacityLimitsCard/types'
import {
  GTSegment,
  GTCreateServiceInput,
  GTLimitType,
  GTCreateRouteServiceItineraryScheduleStopInput,
  GTWeekday,
  GTService,
  GTPassengerPropertiesAutocompleteOutput,
  GTPassengerTypesAutocompleteOutput,
  GTRouteServiceItinerarySchedule,
  GTRouteServiceScheduleFrequencyUnit,
  GTRouteServiceConfirmationModalityV2,
  GTRouteServiceConfirmationModalitiesV2Input,
  GTRouteServiceLimitInput,
  GTTRoute,
  GTCompanyV2Vertical,
  Maybe,
  GTRouteServiceVehicleScheduleDriver,
  GTBusinessClientAutocomplete
} from 'gql/types.generated'
import { filterAndSortData } from 'utils/array'
import { formatISODate, getWeekday, getWeekdayNumber, parseDate } from 'utils/date'
import { ICapacityLimits, IItineraryScheduleInput, IVehicleScheduleInput } from './types'
import { IPlace } from 'features/Places/components/SelectPlaces/types'
import { PASSENGER_TYPE } from 'features/Bookings/components/PassengerType/constants'
import { googleMapsConfig } from 'config/google-maps'
import { IBusinessSiteAutocomplete } from 'features/BusinessSites/components/MultiBusinessSiteAutocomplete'

const { KOLORS_MX_SALES_CHANNEL_ID, POS_MX_SALES_CHANNEL_ID, APP_MX_SALES_CHANNEL_ID } = process.env
export const ALL_OPERATION_DAYS = [1, 2, 3, 4, 5, 6, 7]
export const DEFAULT_FREQUENCY: IVehicleFrequency = { value: 1, unit: GTRouteServiceScheduleFrequencyUnit.Day }
export const APP_MX_SALES_CHANNEL = { id: APP_MX_SALES_CHANNEL_ID ?? '', name: '' }
export const DEFAULT_SALES_CHANNELS = [
  { id: KOLORS_MX_SALES_CHANNEL_ID ?? '', name: '' },
  { id: POS_MX_SALES_CHANNEL_ID ?? '', name: '' },
  APP_MX_SALES_CHANNEL
]

export const vehicleHasDefaultFrequency = (vehicle: IVehicle): boolean => {
  return vehicle.frequency.value === DEFAULT_FREQUENCY.value && vehicle.frequency.unit === DEFAULT_FREQUENCY.unit
}

export const APP_MX_VERTICALS = [GTCompanyV2Vertical.City, GTCompanyV2Vertical.Team]

export const initNewServicesForm = (companyId?: string, route?: GTTRoute): INewServicesForm => {
  return {
    businessClients: route?.businessClient ? [route.businessClient] : [],
    businessSites: route?.businessSite
      ? [
          {
            ...route.businessSite,
            clientId: route.businessClient?.id || ''
          }
        ]
      : [],
    hasTolls: true,
    isPrivate: null,
    routeId: route?.id,
    company: route?.company || { id: companyId, name: '' } || { id: '', name: '' },
    vertical: route?.company.vertical || GTCompanyV2Vertical.Travel,
    companyLine: route?.companyLines[0] || { id: '', name: '' },
    stops: route?.stops || [],
    startDate: new Date(),
    endDate: undefined,
    activePeriod: '',
    //@ts-expect-error FIXME GTCompanyV2Vertical doesn't match APP_MX_VERTICALS
    salesChannels: APP_MX_VERTICALS.includes(route?.company.vertical as unknown as GTCompanyV2Vertical)
      ? [APP_MX_SALES_CHANNEL]
      : DEFAULT_SALES_CHANNELS,
    services: [],
    segments: [],
    limits: [],
    passengers: [],
    properties: [],
    crewUsers: [],
    confirmationModalities: [
      { name: EConfirmationModality.PAID, salesChannels: [], exceptions: { places: [] } },
      { name: EConfirmationModality.RESERVED, salesChannels: [], exceptions: { places: [] } }
    ],
    showSeatsMap: true,
    isPetFriendly: false,
    mapCenterLocation: {
      lat: googleMapsConfig.defaultCenter.LAT,
      lng: googleMapsConfig.defaultCenter.LNG
    },
    encodedShape: route?.polyline?.encoded || '',
    distance: isNumber(route?.polyline?.distance) ? route?.polyline?.distance.toString() : '',
    reason: '',
    peakSettings: [
      {
        startAt: undefined,
        endAt: undefined
      }
    ],
    isOnGuard: false
  }
}

export const mapItinerariesToItinerariesSchedule = (itineraries: IItinerary[]): IItineraryScheduleInput[] => {
  return itineraries.map((itinerary) => {
    let notes: string[] = []
    if (itinerary.note) {
      notes = [itinerary.note]
    }
    return {
      id: itinerary.id ? itinerary.id : undefined,
      routeID: itinerary.routeId,
      startAt: formatISODate(itinerary.startDate as Date),
      endAt: itinerary.endDate ? formatISODate(new Date(itinerary.endDate)) : undefined,
      dow: itinerary.operationDays.map((day) => getWeekday(day)),
      notes,
      itinerary: itinerary.schedule.map(({ id, departure, arrival }, index) => {
        const isOrigin = index === 0
        const isDestination = index === itinerary.schedule.length - 1
        const stopSchedule: GTCreateRouteServiceItineraryScheduleStopInput = {
          placeID: id
        }

        if (!isOrigin) {
          stopSchedule.arrival = {
            time: arrival?.time ?? '',
            day: Number(arrival?.day)
          }
        }
        if (!isDestination) {
          stopSchedule.departure = {
            time: departure?.time ?? '',
            day: Number(departure?.day)
          }
        }

        return stopSchedule
      })
    }
  })
}

export const mapVehiclesToVehiclesSchedule = (vehicles: IVehicle[]): IVehicleScheduleInput[] => {
  const isUsingFrequency = vehicles.some((vehicle) => !vehicleHasDefaultFrequency(vehicle))
  return vehicles.map((vehicle) => ({
    id: vehicle.id ? vehicle.id : undefined,
    vehicleID: vehicle.vehicle.id ?? '',
    driverID: vehicle.driver?.id ? vehicle.driver.id : undefined,
    startAt: vehicle.startDate ? formatISODate(vehicle.startDate) : undefined,
    endAt: vehicle.expirationDate ? formatISODate(vehicle.expirationDate) : undefined,
    dow: !isUsingFrequency ? vehicle.operationDays.map<GTWeekday>((day) => getWeekday(day)) : undefined,
    frequency: isUsingFrequency ? { unit: vehicle.frequency.unit, value: vehicle.frequency.value } : undefined
  }))
}

export const mapConfirmationModalitiesToConfirmationModalitiesInput = (
  confirmationModalities: GTRouteServiceConfirmationModalityV2[]
): GTRouteServiceConfirmationModalitiesV2Input[] => {
  return confirmationModalities.map((modality) => {
    return {
      name: modality.name.toUpperCase(),
      salesChannelIDs: modality.salesChannels.map(({ id }) => id),
      exceptions: {
        placeIDs: modality.exceptions?.places?.map(({ id }) => id) || []
      }
    }
  })
}

interface IServiceBusinessClientInput {
  id: string
  businessSiteIDs: string[]
}

const getServiceBusinessClientsInput = (
  businessClients: GTBusinessClientAutocomplete[],
  businessSites: IBusinessSiteAutocomplete[],
  isPrivate?: boolean,
  vertical?: GTCompanyV2Vertical
): IServiceBusinessClientInput[] => {
  if (vertical === GTCompanyV2Vertical.City && isPrivate) {
    const businessClientsInput = businessSites.reduce<IServiceBusinessClientInput[]>((result, businessSite) => {
      if (!businessSite.clientId) {
        return result
      }
      const businessClient = result.find((client) => client.id === businessSite.clientId)
      if (businessClient) {
        businessClient.businessSiteIDs.push(businessSite.id)
      } else {
        result.push({
          id: businessSite.clientId,
          businessSiteIDs: [businessSite.id]
        })
      }
      return result
    }, [])
    businessClients.forEach((businessClient) => {
      if (!businessClientsInput.some(({ id }) => id === businessClient.id)) {
        businessClientsInput.push({
          id: businessClient.id,
          businessSiteIDs: []
        })
      }
    })
    return businessClientsInput
  } else {
    return []
  }
}

export const mapEditServiceToEditServiceInput = (service: IEditServiceForm): TSFixMe => {
  const editedLimits = [...service.passengers, ...service.properties]
  const salesChannelIds =
    service.vertical === GTCompanyV2Vertical.Rental ? [] : service.salesChannels.map(({ id }) => id)
  const confirmationModalities =
    service.vertical === GTCompanyV2Vertical.Rental
      ? []
      : mapConfirmationModalitiesToConfirmationModalitiesInput(service.confirmationModalities)

  return {
    id: service.id,
    activePeriod: service.activePeriod ? Number(service.activePeriod) : undefined,
    salesChannelIDs: salesChannelIds,
    limits: !isEmpty(editedLimits) ? editedLimits : service.limits,
    userIDs: service.crewUsers ? service.crewUsers.map((user) => user.id) : undefined,
    confirmationModalitiesV2: confirmationModalities,
    isPetFriendly: service.isPetFriendly,
    hasSelectableSeats: service.showSeatsMap,
    isPrivate: service.isPrivate,
    hasTolls: service.hasTolls,
    businessClients: getServiceBusinessClientsInput(
      service.businessClients.map((client) => ({
        id: client.id,
        name: client.name,
        verticals: []
      })),
      service.businessSites,
      service.isPrivate || false,
      service.company.vertical as unknown as GTCompanyV2Vertical
    )
  }
}

export const mapLimitsFormToLimitsInput = (
  limit: ICapacityLimit,
  isServicePetFriendly: boolean
): GTRouteServiceLimitInput => {
  let value
  if (limit.key === PASSENGER_TYPE.PET && !isServicePetFriendly) {
    value = 0
  } else {
    value = limit.isVisible && isFinite(Number(limit.value)) ? Number(limit.value) : null
  }
  return {
    type: GTLimitType.Passenger,
    key: limit.key,
    value
  }
}

export interface IPeakInput {
  id?: string
  startAt: string
  endAt: string
}
export const mapPeaksFormtoPeaksInput = (peaks: IEditServiceForm['peakSettings']): IPeakInput[] => {
  return peaks
    .filter((peak) => peak.startAt && peak.endAt)
    .map((peak) => {
      return {
        id: peak.id || undefined,
        startAt: formatISODate(peak.startAt as Date),
        endAt: peak.endAt ? formatISODate(peak.endAt as Date) : ''
      }
    })
}

export const mapServicesFormToServicesInput = (formData: INewServicesForm): GTCreateServiceInput[] => {
  const salesChannelIds =
    formData.vertical === GTCompanyV2Vertical.Rental ? [] : formData.salesChannels.map(({ id }) => id)
  const confirmationModalities =
    formData.vertical === GTCompanyV2Vertical.Rental
      ? []
      : mapConfirmationModalitiesToConfirmationModalitiesInput(formData.confirmationModalities)
  const services = formData.services.map<GTCreateServiceInput>((service) => {
    const serviceInput: GTCreateServiceInput = {
      companyLineID: formData.companyLine.id,
      startAt: formatISODate(formData.startDate),
      endAt: formData.endDate ? formatISODate(formData.endDate) : undefined,
      activePeriod: formData.activePeriod ? Number(formData.activePeriod) : undefined,
      salesChannelIDs: salesChannelIds,
      code: trim(service.code),
      itinerariesSchedule: mapItinerariesToItinerariesSchedule(service.itineraries),
      vehiclesSchedule: mapVehiclesToVehiclesSchedule(service.vehicles),
      limits: [...formData.passengers, ...formData.properties].map((limit) =>
        mapLimitsFormToLimitsInput(limit, formData.isPetFriendly)
      ),
      userIDs: formData.crewUsers ? formData.crewUsers.map((user) => user.id) : undefined,
      confirmationModalitiesV2: confirmationModalities,
      hasSelectableSeats: formData.showSeatsMap,
      isPetFriendly: formData.isPetFriendly,
      isPrivate: formData.isPrivate ?? false,
      hasTolls: formData.hasTolls,
      businessClients: getServiceBusinessClientsInput(
        formData.businessClients.map((client) => ({
          id: client.id,
          name: client.name,
          verticals: []
        })),
        formData.businessSites,
        formData.isPrivate || false,
        formData.vertical as GTCompanyV2Vertical
      ),
      peaks: mapPeaksFormtoPeaksInput(formData.peakSettings),
      isOnGuard: formData.isOnGuard
    }
    return serviceInput
  })

  return services
}

export const mapItineraryScheduleToItinerary = (itinerarySchedule: GTRouteServiceItinerarySchedule): IItinerary => {
  return {
    id: itinerarySchedule.id,
    status: itinerarySchedule.status,
    routeId: itinerarySchedule.route.id,
    startDate: parseDate(itinerarySchedule.startAt),
    endDate: itinerarySchedule.endAt ? parseDate(itinerarySchedule.endAt) : undefined,
    operationDays: itinerarySchedule.dow.map((day) => getWeekdayNumber(day)),
    schedule: itinerarySchedule.itinerary.map((itinerary, index) => ({
      id: itinerary.placeID,
      name: itinerarySchedule.route.stops[index].name,
      departure: !isEmpty(itinerary.departure)
        ? {
            day: itinerary.departure.day.toString(),
            time: itinerary.departure.time
          }
        : undefined,
      arrival: !isEmpty(itinerary.arrival)
        ? {
            day: itinerary.arrival.day.toString(),
            time: itinerary.arrival.time
          }
        : undefined
    })),
    notes:
      itinerarySchedule.notes?.map((note) => {
        return {
          createdDate: new Date(note.createdAt),
          text: note.text,
          user: {
            id: note.user.id,
            firstName: note.user.firstName,
            lastName: note.user.lastName
          }
        }
      }) || [],
    note: ''
  }
}

export const getDriverName = (driver: Maybe<GTRouteServiceVehicleScheduleDriver> | undefined): string => {
  const driverFirstName = driver?.userAccount?.firstName
  const driverLastName = driver?.userAccount?.lastName

  let name = '-'
  if (driverFirstName && driverLastName) {
    name = `${driverFirstName} ${driverLastName}`
  }

  return name
}

export const mapServiceQueryToEditForm = (service: GTService): IEditServiceForm => {
  const stops = (service.itinerariesSchedule[0]?.route.stops || []).map<IAutocompleteOutput>((stop) => ({
    id: stop.id,
    name: stop.name
  }))

  return {
    id: service.id,
    company: service.companyLine?.company || { id: '', name: '', vertical: '' },
    hasTolls: service.hasTolls,
    isPrivate: service.isPrivate,
    vertical: service.companyLine?.company.vertical as unknown as GTCompanyV2Vertical,
    businessClients:
      service.businessClients?.map((client) => ({
        id: client.id,
        name: client.name
      })) || [],
    businessSites:
      service.businessClients?.reduce<IBusinessSiteAutocomplete[]>((result, client) => {
        return [
          ...result,
          ...(client.businessSites || []).map((site) => ({
            id: site.id,
            name: site.name,
            clientId: client.id
          }))
        ]
      }, []) || [],
    code: service.code ?? '',
    createdAt: new Date(service.createdAt),
    createdBy: service.createdBy?.username ?? '',
    isActive: service.isActive,
    companyId: service.companyLine?.company.id || '',
    startDate: parseDate(service.startAt),
    endDate: service.endAt ? parseDate(service.endAt) : undefined,
    stops: stops,
    activePeriod: service.activePeriod?.toString() || '',
    salesChannels: service.salesChannelsV2 || [],
    services: [
      {
        id: service.id,
        code: service.code ?? '',
        isActive: service.isActive,
        startDate: parseDate(service.startAt),
        priceCheckedDate: service.priceCheckedAt ? new Date(service.priceCheckedAt) : undefined,
        endDate: service.endAt ? parseDate(service.endAt) : undefined,
        activePeriod: service.activePeriod?.toString() || '',
        itineraries: service.itinerariesSchedule.map((itinerarySchedule) =>
          mapItineraryScheduleToItinerary(itinerarySchedule)
        ),
        /*@ts-expect-error: FIXME The types of 'vehicle.id' are incompatible between these types. */
        vehicles: service.vehiclesSchedule
          ? service.vehiclesSchedule.map((vehicleSchedule) => ({
              id: vehicleSchedule.id,
              vehicle: {
                id: vehicleSchedule.vehicle.id,
                name: vehicleSchedule.vehicle.name,
                externalID: vehicleSchedule.vehicle.externalID
              },
              driver: {
                id: vehicleSchedule.driver?.id ?? '',
                name: getDriverName(vehicleSchedule.driver)
              },
              startDate: parseDate(vehicleSchedule.startAt),
              expirationDate: vehicleSchedule.endAt ? parseDate(vehicleSchedule.endAt) : undefined,
              operationDays: vehicleSchedule.dow
                ? vehicleSchedule.dow.map((day) => getWeekdayNumber(day))
                : ALL_OPERATION_DAYS,
              frequency: vehicleSchedule.frequency ?? DEFAULT_FREQUENCY,
              type: vehicleSchedule.frequency ? EVehicleScheduleType.Frequency : EVehicleScheduleType.Dow
            }))
          : []
      }
    ],
    limits: service.limits
      ? service.limits.map<ICapacityLimit>((limit) => ({
          key: limit.key,
          value: isNumber(limit.value) ? limit.value : undefined,
          isVisible: Boolean(limit.value)
        }))
      : [],
    passengers: [],
    properties: [],
    peakSettings: service.peaks?.map((peak) => ({
      id: peak.id,
      startAt: parseDate(peak.startAt),
      endAt: peak.endAt ? parseDate(peak.endAt) : undefined
    })) || [{ startAt: undefined, endAt: undefined }],
    isOnGuard: service.isOnGuard ?? false,
    /*@ts-expect-error: FIXME Type '"RouteServiceUser"' is not assignable to type '"TripUser"'.ts(2322)*/
    crewUsers: service.crewUsers || [],
    /*@ts-expect-error: FIXME The types of 'exceptions.places' are incompatible between these types. */
    confirmationModalities: service.confirmationModalitiesV2
      ? service.confirmationModalitiesV2.map((confirmationModality) => {
          return {
            ...confirmationModality,
            name: confirmationModality.name.toLowerCase()
          }
        })
      : [],
    showSeatsMap: isBoolean(service.hasSelectableSeats) ? service.hasSelectableSeats : true,
    isPetFriendly: service.isPetFriendly || false
  }
}

const getEmptySchedule = (stops: IAutocompleteOutput[]): IItinerarySegment[] => {
  return stops.map((stop) => ({
    id: stop.id,
    name: stop.name,
    departure: { time: '', day: '0' },
    arrival: { time: '', day: '0' }
  }))
}

export const getEmptyItinerary = (
  routeId: string,
  stops: IAutocompleteOutput[],
  startDate?: Date,
  endDate?: Date
): IItinerary => ({
  routeId,
  startDate: startDate,
  endDate: endDate,
  operationDays: ALL_OPERATION_DAYS,
  schedule: getEmptySchedule(stops),
  notes: [],
  note: ''
})

export const getEmptyVehicle = (startDate?: Date, endDate?: Date): IVehicle => ({
  vehicle: { id: '', name: '' },
  startDate: startDate,
  expirationDate: endDate,
  operationDays: ALL_OPERATION_DAYS,
  frequency: DEFAULT_FREQUENCY,
  type: EVehicleScheduleType.Dow,
  driver: { id: '', name: '' }
})

export const getEmptyService = (
  routeId: string,
  route: IAutocompleteOutput[],
  startDate?: Date,
  endDate?: Date
): IService => {
  return {
    code: '',
    startDate,
    endDate,
    itineraries: [getEmptyItinerary(routeId, route, startDate, endDate)],
    vehicles: [getEmptyVehicle(startDate, endDate)]
  }
}

export const getSegments = (companyLine: IAutocompleteOutput, segments: GTSegment[]): ISegmentPlanning[] => {
  const segmentsPlanning = segments.map((segment) => {
    const segmentCode = `${segment.origin.name} (${segment.origin.code}) - ${segment.destination.name} (${segment.destination.code})`
    const spect = segment.companyLineSpecs.find((spec) => spec.companyLine.id === companyLine.id)
    const hours = isNumber(spect?.duration) ? Math.floor((spect?.duration as number) / 60) : null
    const minutes = isNumber(spect?.duration) ? (spect?.duration as number) % 60 : null
    const distance = isNumber(spect?.distance) ? (spect?.distance as number) / 1000 : null

    return {
      id: segment.id,
      name: segmentCode,
      hours,
      minutes,
      distance,
      isExistingSpec: Boolean(spect)
    }
  })

  return segmentsPlanning
}

export const getEmptyCapacityLimit = (key: string): ICapacityLimit => ({
  key,
  value: undefined,
  isVisible: false
})

export const getCapacityLimits = (
  passengerTypes: GTPassengerTypesAutocompleteOutput[] = [],
  passengerProperties: GTPassengerPropertiesAutocompleteOutput[] = []
): ICapacityLimits => {
  const filteredPassengerTypes = filterAndSortData(passengerTypes)
  const filteredPassengerProperties = filterAndSortData(passengerProperties)
  return {
    passengers: filteredPassengerTypes.map((passengerType) => getEmptyCapacityLimit(passengerType.name)),
    properties: filteredPassengerProperties.map((passengerProperty) => getEmptyCapacityLimit(passengerProperty.name))
  }
}

const initPlanningByItinerary = (itinerary: IItinerary): IPlanning => {
  const stops = itinerary.schedule.map(({ id, name }) => {
    return { id, name }
  })
  return {
    itineraries: [itinerary],
    startDate: itinerary.startDate as Date,
    endDate: itinerary.endDate ?? undefined,
    routeId: itinerary.routeId,
    stops
  }
}

export const getPlanningsByService = (service: IService): IPlanning[] => {
  return service.itineraries.reduce((result: IPlanning[], itinerary) => {
    const lastPlanning = result.at(-1)
    if (!lastPlanning) {
      result = [initPlanningByItinerary(itinerary)]
    } else if (lastPlanning.routeId === itinerary.routeId) {
      result[result.length - 1] = {
        ...lastPlanning,
        endDate: itinerary.endDate ?? undefined,
        itineraries: [...lastPlanning.itineraries, itinerary]
      }
    } else {
      result = [...result, initPlanningByItinerary(itinerary)]
    }
    return result
  }, [])
}

const getValidStartDate = (
  currentStartDate: Date | undefined,
  startDateLimit?: Date,
  endDateLimit?: Date
): Date | undefined => {
  if (!currentStartDate) {
    return startDateLimit
  }
  if (startDateLimit && isBefore(currentStartDate, startDateLimit)) {
    return startDateLimit
  } else if (endDateLimit && isAfter(currentStartDate, endDateLimit)) {
    return endDateLimit
  }
  return currentStartDate
}

const getValidEndDate = (
  currentEndDate: Date | undefined,
  startDateLimit?: Date,
  endDateLimit?: Date
): Date | undefined => {
  if (!currentEndDate) {
    if (endDateLimit) {
      return endDateLimit
    }
  } else if (endDateLimit && isAfter(currentEndDate, endDateLimit)) {
    return endDateLimit
  } else if (startDateLimit && isBefore(currentEndDate, startDateLimit)) {
    return startDateLimit
  }
  return currentEndDate
}

export const updateServiceByRouteAndRangedDate = (
  service: IService,
  routeId: string,
  stops: IAutocompleteOutput[],
  limitStartDate?: Date,
  limitEndDate?: Date
): IService => {
  return {
    ...service,
    startDate: limitStartDate,
    endDate: limitEndDate,
    itineraries: service.itineraries.map((itinerary, index) => {
      const isFirstIitnerary = index === 0
      const currentStopsIds = itinerary.schedule.map(({ id }) => id)
      const newStopsIds = stops.map(({ id }) => id)
      const isStopsChanged = !isEqual(currentStopsIds, newStopsIds)
      return {
        ...itinerary,
        routeId,
        startDate: isFirstIitnerary
          ? limitStartDate
          : getValidStartDate(itinerary.startDate, limitStartDate, limitEndDate),
        endDate: getValidEndDate(itinerary.endDate ?? undefined, limitStartDate, limitEndDate),
        schedule: isStopsChanged ? getEmptySchedule(stops) : itinerary.schedule
      }
    }),
    vehicles: service.vehicles.map((vehicle, index) => {
      const isFirstVehicle = index === 0
      const isLastVehicle = index === service.vehicles.length - 1
      return {
        ...vehicle,
        startDate: isFirstVehicle ? limitStartDate : getValidStartDate(vehicle.startDate, limitStartDate, limitEndDate),
        expirationDate: isLastVehicle
          ? limitEndDate
          : getValidEndDate(vehicle.expirationDate, limitStartDate, limitEndDate)
      }
    })
  }
}

export const getFrequencyDaysCount = (frequency: IVehicleFrequency): number => {
  if (frequency.unit === GTRouteServiceScheduleFrequencyUnit.Week) {
    return frequency.value * 7
  }
  return frequency.value
}

export const mapDayToOperationDay = (day: number): number => {
  if (day === 0) {
    return 7
  }
  return day
}

export const operationDaysMatchDate = (operationDays: number[], date: Date): boolean => {
  return operationDays.includes(mapDayToOperationDay(date.getDay()))
}

export const getOriginsByItineraries = (itineraries: IItinerary[]): IPlace[] => {
  return itineraries.reduce<IPlace[]>((result, itinerary) => {
    itinerary.schedule.forEach((placeSchedule, placeScheduleIndex) => {
      const placeIsAlreadyAdded = result.some((place) => place.id === placeSchedule.id)
      const isOrigin = placeScheduleIndex !== itinerary.schedule.length - 1
      if (isOrigin && !placeIsAlreadyAdded) {
        result = [...result, { id: placeSchedule.id, name: placeSchedule.name }]
      }
    })
    return result
  }, [])
}
